import {useQuantityFormat} from '@aboutyou/i18n';
import {styled as linariaStyled} from '@linaria/react';
import React, {useCallback} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {mediaTabletAndUp} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';

type TabBackgroundColour = string;

interface TabProps<T> {
  className?: string;
  style?: React.CSSProperties;
  buttonComponent?: React.ComponentType<any>;
  dataTestId?: string;
  onTabClick: (tabValue: T) => void;
  selectedTabValue?: T;
  tabs: Array<{
    'data-testid'?: string;
    label: React.ReactNode;
    value: T;
    count?: number;
    backgroundColour?: TabBackgroundColour;
  }>;
  reduceFontSize?: boolean;
  withBorder?: boolean;
  highlightColorOnActive?: boolean;
  activeTextColor?: keyof typeof COLORS;
  inactiveTextColor?: keyof typeof COLORS;
  showInactiveBorder?: boolean;
}

export const Tabs = <T extends string | number>({
  buttonComponent,
  dataTestId = 'tabs',
  onTabClick,
  selectedTabValue,
  tabs,
  reduceFontSize,
  withBorder,
  highlightColorOnActive,
  activeTextColor = 'Black',
  inactiveTextColor = 'Black',
  showInactiveBorder,
  ...props
}: TabProps<T>) => {
  const getNumberFormat = useQuantityFormat();
  const getBackgroundColour = useCallback(
    (active: boolean, backgroundColour?: TabBackgroundColour) => {
      if (!active) {
        return `background-color: ${COLORS.Grey2};`;
      }

      if (typeof backgroundColour === 'string') {
        return `background-color: ${backgroundColour || COLORS.Black};`;
      }

      return `background-color: ${COLORS.Black};`;
    },
    [tabs],
  );

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Wrapper
      // @ts-ignore
      mode={withBorder ? 'bordered' : undefined}
      data-testid={dataTestId}
      {...props}
    >
      {tabs.map((tab, index) => {
        const active = tab.value === selectedTabValue;
        const tabId = `${dataTestId}_tab_${index}${active ? '_selected' : ''}`;
        const backgroundColour = getBackgroundColour(
          active,
          tab.backgroundColour,
        );
        const textColor = active
          ? COLORS[activeTextColor]
          : COLORS[inactiveTextColor];

        return (
          <Tab
            mode={withBorder ? 'bordered' : undefined}
            withBorder={withBorder}
            active={active}
            as={buttonComponent}
            data-testid={tab['data-testid'] || tabId}
            flex={1 / (tabs.length ?? 1)}
            key={tabId}
            onClick={() => onTabClick(tab.value)}
            reduceFontSize={reduceFontSize}
            highlightColorOnActive={highlightColorOnActive}
            $textColor={textColor}
            $showInactiveBorder={showInactiveBorder}
          >
            {tab.label}
            {tab.count !== undefined && (
              <TabCount $backgroundColour={backgroundColour}>
                {getNumberFormat(tab.count)}
              </TabCount>
            )}
          </Tab>
        );
      })}
    </Wrapper>
  );
};

const Wrapper = linariaStyled.div`
  box-sizing: border-box;
  display: flex;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.bold};
  height: 50px;
  width: 100%;
  background-color: ${COLORS.White};
`;

const TabCount = styled.div<{
  $backgroundColour?: string;
}>`
  margin-left: 10px;
  border-radius: 9px;
  color: ${COLORS.White};

  ${({$backgroundColour}) => $backgroundColour}

  min-width: 20px;
  padding: 0 5px;
  height: 20px;
  font-size: 12px;
  display: block;
  text-align: center;
  line-height: 19px;
  flex-shrink: 0;
`;

const Tab = styled.button<{
  active?: boolean;
  flex: number;
  reduceFontSize?: boolean;
  withBorder?: boolean;
  highlightColorOnActive?: boolean;
  $textColor: string;
  $showInactiveBorder?: boolean;
}>`
  align-items: center;
  background-color: transparent;
  border: none;
  border-bottom-style: solid;
  border-bottom-width: 2px;
  border-bottom-color: ${({active, $showInactiveBorder}) => {
    if (active) {
      return COLORS.Black;
    }
    return $showInactiveBorder ? COLORS.Grey4 : 'transparent';
  }};
  color: ${({$textColor}) => $textColor};
  cursor: pointer;
  display: flex;
  flex: ${({flex}) => flex};
  font-size: inherit;
  font-weight: inherit;
  justify-content: center;
  text-align: center;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: border 0.15s;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px auto Highlight;
    outline: 2px auto -webkit-focus-ring-color;
  }

  ${({highlightColorOnActive, active}) =>
    highlightColorOnActive &&
    css`
      color: ${active ? COLORS.Black : COLORS.Grey2};
    `}

  ${({withBorder, active}) =>
    withBorder &&
    css`
      border: 1px solid ${active ? COLORS.Black : COLORS.Grey2};
      color: ${active ? COLORS.Black : COLORS.Grey2};
      border-radius: 4px;
      flex: none;
      padding: 12px 18px;
      width: 148px;
      font-size: 12px;

      @media ${mediaTabletAndUp} {
        height: 32px;
      }
    `}
`;
